import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import type { CreativeEntry } from 'types/shared';
import type { CreateTwitterAdParams, TwitterAdSettingsPayload, TwitterCampaignWithLineItems } from '../api';
import { buildCreateTwitterAdPayload } from '../utils/twitterAdSubmitPayload';
import { createTwitterAdSchema } from '../validation/createTwitterAdSchema.schema';

interface UseCreateTwitterAdParamsReturn {
  params: CreateTwitterAdParams;
  isValid: boolean;
  errors: string[];
}

const EMPTY_ROWS: CreativeEntry[] = [];
const EMPTY_LINE_ITEMS: TwitterCampaignWithLineItems[] = [];
const EMPTY_IDS: string[] = [];
const EMPTY_SETTINGS: Record<string, TwitterAdSettingsPayload | undefined> = {};

const useCreateTwitterAdParams = (): UseCreateTwitterAdParamsReturn => {
  useFormContext();
  const app = (useWatch({ name: 'app' }) as string | undefined) ?? '';
  const creativesRows = (useWatch({ name: '$creativesRows' }) as CreativeEntry[] | undefined) ?? EMPTY_ROWS;
  const lineItemsData =
    (useWatch({ name: '$lineItemsData' }) as TwitterCampaignWithLineItems[] | undefined) ?? EMPTY_LINE_ITEMS;
  const selectedLineItems = (useWatch({ name: 'selectedLineItems' }) as string[] | undefined) ?? EMPTY_IDS;
  const adSettings =
    (useWatch({ name: 'adSettings' }) as Record<string, TwitterAdSettingsPayload | undefined> | undefined) ??
    EMPTY_SETTINGS;

  const params = useMemo(
    () =>
      buildCreateTwitterAdPayload({
        app,
        creativesRows,
        lineItemsData,
        selectedLineItems,
        adSettings,
      }),
    [app, creativesRows, lineItemsData, selectedLineItems, adSettings],
  );

  const validation = useMemo(() => {
    try {
      createTwitterAdSchema.validateSync(params, { abortEarly: false });
      return { isValid: true, errors: [] };
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        return { isValid: false, errors: error.errors };
      }
      return { isValid: false, errors: ['Unknown validation error occurred'] };
    }
  }, [params]);

  return { params, ...validation };
};

export default useCreateTwitterAdParams;
